/**
 * Printable client monthly report — /admin/riport/:userId?year=YYYY&month=M
 *
 * Fetches from GET /api/admin/client-report/{user_id}, renders the exact
 * same layout as the email HTML but framed for the browser + browser print
 * (window.print → save as PDF). Admin can also trigger a live email send
 * with the "Send" button.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ChevronLeft, Printer, Mail, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const ADMIN_KEY_STORAGE = 'orosz_admin_key_v1';
const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = ['', 'január', 'február', 'március', 'április', 'május', 'június',
                    'július', 'augusztus', 'szeptember', 'október', 'november', 'december'];

export default function AdminClientReportPage() {
  const nav = useNavigate();
  const { userId } = useParams();
  const [params, setParams] = useSearchParams();
  const adminKey = localStorage.getItem(ADMIN_KEY_STORAGE) || '';

  const now = new Date();
  const defYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const defMonth = now.getMonth() === 0 ? 12 : now.getMonth();

  const year = parseInt(params.get('year') || defYear, 10);
  const month = parseInt(params.get('month') || defMonth, 10);

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!adminKey) { nav('/admin'); return; }
    setLoading(true);
    axios.get(`${API}/api/admin/client-report/${userId}?year=${year}&month=${month}`,
      { headers: { 'X-Admin-Key': adminKey } })
      .then(r => setReport(r.data))
      .catch(e => toast.error('Hiba: ' + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [userId, year, month, adminKey, nav]);

  const sendEmail = async () => {
    if (!window.confirm(`Elküldöd az emailt ide: ${report.user.email}?`)) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/client-report/${userId}/send`,
        { year, month, force: false },
        { headers: { 'X-Admin-Key': adminKey } },
      );
      if (r.data.sent) toast.success('Email elküldve!');
      else if (r.data.reason === 'already_sent') toast.message('Ehhez a hónaphoz már ki lett küldve. Használd a „Kényszerített küldés"-t ha újra kell.');
      else toast.error('A küldés nem sikerült.');
    } catch (e) {
      toast.error('Hiba: ' + (e?.response?.data?.detail || e.message));
    }
    setSending(false);
  };

  const forceSend = async () => {
    if (!window.confirm(`FIGYELEM: Kényszerített küldés. Az email újra ki lesz küldve ide: ${report.user.email}. Biztos?`)) return;
    setSending(true);
    try {
      const r = await axios.post(
        `${API}/api/admin/client-report/${userId}/send`,
        { year, month, force: true },
        { headers: { 'X-Admin-Key': adminKey } },
      );
      if (r.data.sent) toast.success('Email újraküldve.');
      else toast.error('A küldés nem sikerült.');
    } catch (e) {
      toast.error('Hiba: ' + (e?.response?.data?.detail || e.message));
    }
    setSending(false);
  };

  const changeMonth = (dm) => {
    let y = year, m = month + dm;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setParams({ year: y, month: m });
  };

  if (loading) return <div className="min-h-screen bg-gray-950 flex items-center justify-center text-white"><Loader2 className="animate-spin" /></div>;
  if (!report) return <div className="min-h-screen bg-gray-950 text-white p-8">Nincs riport.</div>;

  const w = report.weight, wa = report.waist;
  const deltaColor = (d) => d == null ? '#9ca3af' : d <= 0 ? '#059669' : (Math.abs(d) < 1 ? '#d97706' : '#dc2626');
  const sign = (d) => d > 0 ? '+' : '';

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      <style>{`@media print {
        @page { size: A4; margin: 12mm; }
        .no-print { display: none !important; }
        body { background: white !important; }
      }`}</style>

      <header className="no-print border-b border-gray-800 bg-gray-900 text-white sticky top-0 z-10">
        <div className="container mx-auto px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to={`/admin/haladas?user=${userId}`} className="text-gray-400 hover:text-white"><ChevronLeft size={20} /></Link>
            <Sparkles className="text-yellow-500" size={18} />
            <h1 className="text-sm font-bold">Havi riport előnézet</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => changeMonth(-1)} className="text-xs px-2 py-1 bg-gray-800 rounded hover:bg-gray-700">◀ Előző</button>
            <span className="text-xs text-gray-300 min-w-[110px] text-center">{year}. {MONTHS[month]}</span>
            <button onClick={() => changeMonth(1)} className="text-xs px-2 py-1 bg-gray-800 rounded hover:bg-gray-700">Következő ▶</button>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => window.print()} size="sm" variant="outline" className="border-gray-700 text-gray-300 hover:bg-gray-800">
              <Printer size={14} className="mr-1.5" /> PDF
            </Button>
            <Button onClick={sendEmail} disabled={sending} size="sm" className="bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold">
              {sending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Mail size={14} className="mr-1.5" />}
              Email küldése
            </Button>
            <button onClick={forceSend} disabled={sending} className="text-xs text-gray-500 hover:text-amber-400 px-2" title="Már elküldött hónap újraküldése">
              újra
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto py-8 print:py-0 max-w-2xl">
        <div className="bg-white rounded-lg shadow-lg print:shadow-none p-8 print:p-4" data-testid="client-report-content">
          {/* Header */}
          <div className="text-center border-b-2 border-yellow-600 pb-4">
            <div className="text-yellow-600 text-xs font-bold uppercase tracking-widest">Orosz Coaching</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">Havi haladási riport</div>
            <div className="text-sm text-gray-500 mt-1">{report.period.label} · {report.user.name}</div>
          </div>

          <p className="text-base text-gray-800 mt-6">Szia <strong>{report.user.name}</strong>! 👋</p>
          <p className="text-sm text-gray-700 leading-relaxed mt-2">
            Íme a {report.period.label}-i haladásod összefoglalója. Minden szám a saját munkádé — büszke vagyok Rád! 💪
          </p>

          {report.headline && (
            <div className="mt-5 p-4 bg-amber-50 border-l-4 border-yellow-600 rounded">
              <div className="text-sm font-semibold text-amber-900">{report.headline}</div>
            </div>
          )}

          {/* Stats grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="text-xs uppercase tracking-wider text-gray-500">Elvégzett edzések</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">{report.sessions.count}</div>
              <div className="text-xs text-gray-600 mt-1">{report.sessions.distinct_exercises} különböző gyakorlat</div>
            </div>

            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="text-xs uppercase tracking-wider text-gray-500">Testsúly</div>
              {w.delta != null ? (
                <>
                  <div className="text-2xl font-bold mt-1" style={{ color: deltaColor(w.delta) }}>{sign(w.delta)}{w.delta} kg</div>
                  <div className="text-xs text-gray-600 mt-1">{w.first}kg → {w.last}kg</div>
                </>
              ) : <div className="text-gray-400 mt-1">Még gyűjtjük</div>}
            </div>

            <div className="p-4 border border-gray-200 rounded-lg">
              <div className="text-xs uppercase tracking-wider text-gray-500">Derék</div>
              {wa.delta != null ? (
                <>
                  <div className="text-2xl font-bold mt-1" style={{ color: deltaColor(wa.delta) }}>{sign(wa.delta)}{wa.delta} cm</div>
                  <div className="text-xs text-gray-600 mt-1">{wa.first}cm → {wa.last}cm</div>
                </>
              ) : <div className="text-gray-400 mt-1">Még gyűjtjük</div>}
            </div>
          </div>

          {report.best_lift && (
            <div className="mt-5 p-4 rounded-lg" style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)' }}>
              <div className="text-xs uppercase tracking-wider font-bold text-amber-800">🏆 Hónap kiemelt teljesítménye</div>
              <div className="text-lg font-bold text-amber-900 mt-1">{report.best_lift.exercise}</div>
              <div className="text-sm text-amber-800">+{report.best_lift.change_pct}% erőnövekedés — {report.best_lift.message}</div>
            </div>
          )}

          <div className="mt-8 p-5 bg-gray-50 rounded-lg text-center">
            <div className="italic text-gray-700 leading-relaxed">
              &ldquo;A haladás nem mindig gyors, de mindig valós. Minden edzés egy lépés a jövőd felé.&rdquo;
            </div>
            <div className="text-sm text-gray-500 mt-2">— Orosz Gábor</div>
          </div>

          <div className="mt-6 text-center no-print">
            <a href="https://oroszcoaching.hu/fiokom" className="inline-block bg-gray-900 text-yellow-400 px-6 py-3 rounded font-bold text-sm">
              Belépek a fiókomba →
            </a>
          </div>

          <div className="mt-8 pt-4 border-t border-gray-200 text-xs text-center text-gray-500 leading-relaxed">
            Ez a riport minden hónap elején automatikusan érkezik.<br />
            Ha kérdésed van, válaszolj erre az emailre, és Orosz Gábor személyesen jelentkezik.
          </div>
        </div>
      </main>
    </div>
  );
}
